import React, { useRef, useState } from "react";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Captcha from "@/components/forms/Captcha";
import api from "@/lib/api";

export default function Contact() {
  React.useEffect(() => {
    document.title = "Contact - Pune Spa Guide";
  }, []);

  const [f, setF] = useState({ name: "", email: "", message: "" });
  // Honeypot — real users never touch this. Automated bots often fill in
  // every input they find, so a non-empty value → we reject the submission.
  const [website, setWebsite] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Client-side captcha guard — the backend enforces this too, but failing
    // fast here saves a round-trip and gives clearer error copy.
    if (!captchaToken) {
      toast.error("Please complete the captcha to send your message.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: f.name.trim(),
        email: f.email.trim(),
        message: f.message.trim(),
        hcaptcha_token: captchaToken,
        website, // honeypot
      });
      toast.success("Thanks! Your message has been sent. We'll reply within 24 hours.");
      setF({ name: "", email: "", message: "" });
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again in a moment.";
      toast.error(String(msg));
      // Force a fresh captcha token after a rejected submit
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Get in touch</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2 text-[#111827]">Contact Us</h1>
          <p className="mt-3 text-[#4B5563]">For listings, partnerships, feedback or media inquiries.</p>
          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-[#1F4E3D]" />
              <a
                href="mailto:hello@punespaguide.com"
                className="hover:text-[#1F4E3D] hover:underline"
                data-testid="contact-email-link"
              >
                hello@punespaguide.com
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={16} className="text-[#1F4E3D]" /> +91 7378513961
            </div>
            <div className="flex items-center gap-3">
              <MapPin size={16} className="text-[#1F4E3D]" /> Pune
            </div>
          </div>
        </div>
        <form
          onSubmit={submit}
          className="rounded-2xl bg-[#F5EFE6] border border-[#E7DDCF] p-6 space-y-4"
          data-testid="contact-form"
        >
          <div>
            <Label className="text-xs" htmlFor="contact-name">Name</Label>
            <Input
              id="contact-name"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              required
              maxLength={120}
              disabled={submitting}
              data-testid="contact-name-input"
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              type="email"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
              required
              disabled={submitting}
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="contact-message">Message</Label>
            <Textarea
              id="contact-message"
              rows={5}
              value={f.message}
              onChange={(e) => setF({ ...f, message: e.target.value })}
              required
              minLength={5}
              maxLength={2000}
              disabled={submitting}
              data-testid="contact-message-input"
            />
            <div className="text-[11px] text-[#7A5A22] mt-1 text-right">{f.message.length}/2000</div>
          </div>

          {/* Hidden honeypot — visually removed but focusable by bots that
              scrape every input.  Real users don't see it. */}
          <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          <Captcha ref={captchaRef} onToken={setCaptchaToken} testId="contact-captcha" />

          <Button
            type="submit"
            data-testid="contact-form-submit-button"
            disabled={submitting}
            className="bg-[#111827] hover:bg-[#0B1220] text-white w-full h-11 gap-2 disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending…
              </>
            ) : (
              "Send Message"
            )}
          </Button>
          <p className="text-[11px] text-[#7A5A22] text-center">
            Your message is delivered to <strong>hello@punespaguide.com</strong>. We reply within 24 hours on business days.
          </p>
        </form>
      </div>
    </div>
  );
}
